import { defineStore } from 'pinia'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'
import { apiMutate } from '../composables/apiMutate'

// Admin-only store for the owner's CV contact block: the one persistent
// record every rendered CV is built against. The server returns empty
// defaults before the row exists, so `profile` is never null once loaded.

export interface CvProfile {
  name: string | null
  location: string | null
  email: string | null
  linkedin_url: string | null
  github_url: string | null
  birth_date: string | null
  published: boolean
  updated_at: string | null
}

export type CvProfileFields = Pick<
  CvProfile,
  'name' | 'location' | 'email' | 'linkedin_url' | 'github_url' | 'birth_date'
>

export const EMPTY_CV_PROFILE: CvProfile = {
  name: null,
  location: null,
  email: null,
  linkedin_url: null,
  github_url: null,
  birth_date: null,
  published: false,
  updated_at: null,
}

export const useCvProfileStore = defineStore('cvProfile', () => {
  const {
    data: profile,
    loaded,
    loading,
    error: loadError,
    refresh: fetchProfile,
  } = useAsyncResource<CvProfile>(
    async () => {
      const res = await fetch('/api/v1/cv_profile', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load CV profile: ${res.status}`)
      return res.json()
    },
    { initialValue: EMPTY_CV_PROFILE },
  )

  async function save(fields: CvProfileFields): Promise<CvProfile> {
    const updated = await apiMutate<CvProfile>('/api/v1/cv_profile', {
      method: 'PATCH',
      body: { cv_profile: fields },
      failureLabel: 'failed to save CV profile',
    })
    profile.value = updated
    return updated
  }

  async function setPublished(published: boolean): Promise<CvProfile> {
    const updated = await apiMutate<CvProfile>('/api/v1/cv_profile', {
      method: 'PATCH',
      body: { cv_profile: { published } },
      failureLabel: published ? 'failed to publish the CV' : 'failed to unpublish the CV',
    })
    profile.value = updated
    return updated
  }

  return { profile, loaded, loading, loadError, fetchProfile, save, setPublished }
})
